import { useEffect, useMemo, useState } from 'react';
import type { ImportAssignment } from '@tla/types';
import { api, type ImportPreview } from '../api/client';
import { useWorkspaceStore } from '../store/workspaceStore';
import { LAKESIDE } from '../tracks/lakeside';
import { formatLapTime } from '../util/format';
import { EntityPicker, NEW, usePickerState } from './EntityPicker';

/**
 * Two-step import: (1) upload + parse -> preview, (2) user assigns
 * event/track/car/driver/stint and confirms to create a session. Metadata is
 * never inferred from the filename; the CSV track name only pre-selects a track.
 */
export function ImportDialog(props: { onClose: () => void }): React.ReactElement {
  const store = useWorkspaceStore();
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [today] = useState(() => new Date().toISOString().slice(0, 10));

  const event = usePickerState();
  const track = usePickerState();
  const car = usePickerState();
  const driver = usePickerState();
  const team = usePickerState();
  const [eventDate, setEventDate] = useState(today);
  const [stint, setStint] = useState('1');
  const [carNumber, setCarNumber] = useState('');

  const tracks = useMemo(() => Object.values(store.tracks), [store.tracks]);
  const events = useMemo(() => Object.values(store.events), [store.events]);
  const cars = useMemo(() => Object.values(store.cars), [store.cars]);
  const drivers = useMemo(() => Object.values(store.drivers), [store.drivers]);

  // Existing team names come from prior sessions (team is a per-session
  // attribute), fetched once so the picker can suggest them.
  const [teamNames, setTeamNames] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api.listSessions().then((rows) => {
      if (cancelled) return;
      setTeamNames([...new Set(rows.map((s) => s.team).filter((t): t is string => !!t))].sort());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Default to the existing Lakeside track (selected, not a pre-filled new-track
  // name). The CSV track hint may still re-point this once a file is parsed.
  const trackSetId = track.setId;
  const trackHasSelection = track.id !== '';
  useEffect(() => {
    if (trackHasSelection) return;
    const lakeside = tracks.find((t) => t.name.toLowerCase().includes('lakeside'));
    if (lakeside) trackSetId(lakeside.id);
  }, [tracks, trackHasSelection, trackSetId]);

  async function onFile(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const p = await api.importFile(file);
      setPreview(p);
      // Soft-hint: pre-select a track whose name matches the CSV track name.
      if (p.trackHint) {
        const match = tracks.find(
          (t) => t.name.toLowerCase() === p.trackHint?.toLowerCase(),
        );
        if (match) track.setId(match.id);
        else {
          track.setId(NEW);
          track.setNewName(p.trackHint);
        }
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /** Resolve or create the track, returning its id. */
  async function resolveTrackId(): Promise<string> {
    if (track.id && track.id !== NEW) return track.id;
    const name = track.newName.trim() || preview?.trackHint || 'New track';
    // Seed Lakeside geometry when the name matches; otherwise a bare track.
    const isLakeside = name.toLowerCase() === 'lakeside';
    const created = await api.createTrack(
      isLakeside ? { ...LAKESIDE, name } : { name },
    );
    return created.id;
  }

  async function confirm(): Promise<void> {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const trackId = await resolveTrackId();

      const assignment: ImportAssignment = { stint: stint.trim() || '1', trackId };

      if (event.id && event.id !== NEW) assignment.eventId = event.id;
      else assignment.newEvent = { name: event.newName.trim() || 'New event', date: eventDate, trackId };

      if (driver.id && driver.id !== NEW) assignment.driverId = driver.id;
      else assignment.newDriver = { name: driver.newName.trim() || 'New driver' };

      if (car.id && car.id !== NEW) assignment.carId = car.id;
      else
        assignment.newCar = {
          name: car.newName.trim() || 'New car',
        };
      const teamName = team.id === NEW ? team.newName.trim() : team.id;
      if (teamName) assignment.carTeam = teamName;
      if (carNumber.trim()) assignment.carNumber = carNumber.trim();

      const session = await api.confirmImport(preview.importId, assignment);
      await store.refreshEntities();
      await store.selectEvent(session.eventId);
      props.onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="overlay" onClick={props.onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <h2>Import session</h2>

        {!preview && (
          <DropZone
            busy={busy}
            onFile={(f) => {
              void onFile(f);
            }}
          />
        )}

        {preview && (
          <>
            <div className="small muted" style={{ marginBottom: 10 }}>
              <strong>{preview.sourceFile}</strong> · {preview.channels.length} channels ·{' '}
              {preview.validLapCount}/{preview.lapCount} laps · best{' '}
              {formatLapTime(preview.bestLapTime)} · {Math.round(preview.durationSec)}s
              {preview.trackHint && ` · track hint: ${preview.trackHint}`}
            </div>

            <EntityPicker
              label="Track"
              options={tracks.map((t) => ({ id: t.id, label: t.name }))}
              value={track.id}
              onChange={track.setId}
              newName={track.newName}
              onNewName={track.setNewName}
              newPlaceholder="Track name"
            />

            <div className="row">
              <EntityPicker
                label="Event"
                options={events.map((e) => ({ id: e.id, label: `${e.name} (${e.date})` }))}
                value={event.id}
                onChange={event.setId}
                newName={event.newName}
                onNewName={event.setNewName}
                newPlaceholder="Event name"
              />
              {event.id === NEW && (
                <div className="field" style={{ maxWidth: 150 }}>
                  <label>Date</label>
                  <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
                </div>
              )}
            </div>

            <div className="row">
              <EntityPicker
                label="Driver"
                options={drivers.map((d) => ({ id: d.id, label: d.name }))}
                value={driver.id}
                onChange={driver.setId}
                newName={driver.newName}
                onNewName={driver.setNewName}
                newPlaceholder="Driver name"
              />
              <EntityPicker
                label="Car"
                options={cars.map((c) => ({ id: c.id, label: c.name }))}
                value={car.id}
                onChange={car.setId}
                newName={car.newName}
                onNewName={car.setNewName}
                newPlaceholder="Car name"
              />
              <div className="field" style={{ maxWidth: 90 }}>
                <label>Stint</label>
                <input value={stint} onChange={(e) => setStint(e.target.value)} />
              </div>
            </div>

            <div className="row">
              <EntityPicker
                label="Team"
                options={teamNames.map((t) => ({ id: t, label: t }))}
                value={team.id}
                onChange={team.setId}
                newName={team.newName}
                onNewName={team.setNewName}
                newPlaceholder="Team name"
              />
              <div className="field" style={{ maxWidth: 110 }}>
                <label>Car number</label>
                <input
                  placeholder="e.g. 46"
                  value={carNumber}
                  onChange={(e) => setCarNumber(e.target.value)}
                />
              </div>
            </div>
          </>
        )}

        {error && <div className="error">{error}</div>}

        <div className="dialog-actions">
          <button onClick={props.onClose} disabled={busy}>
            Cancel
          </button>
          {preview && (
            <button className="primary" onClick={() => void confirm()} disabled={busy}>
              {busy ? 'Saving…' : 'Create session'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DropZone(props: { busy: boolean; onFile: (f: File) => void }): React.ReactElement {
  const [over, setOver] = useState(false);
  return (
    <label
      className="dropzone"
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files[0];
        if (f) props.onFile(f);
      }}
      style={{
        display: 'block',
        border: `2px dashed ${over ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 8,
        padding: '40px 20px',
        textAlign: 'center',
        cursor: 'pointer',
        color: 'var(--text-dim)',
      }}
    >
      {props.busy ? 'Parsing…' : 'Drop a RaceChrono .csv or .rcz file here, or click to browse'}
      <input
        type="file"
        accept=".csv,.rcz"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) props.onFile(f);
        }}
      />
    </label>
  );
}
